/**
 * ============================================================================
 * StartWorkflowModal — submit a draft for review
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: Phase 1 closure (T-P1-W3-06). Author submits an existing draft
 *          into the FSM via POST /api/v1/workflows with a WF-STD-3 / WF-STD-1
 *          / WF-FAST-CRITICAL template + reviewer/QA assignments.
 *
 *          The action is idempotent on (tenant, Idempotency-Key) with a
 *          24h replay window (WF-008). A double-click MUST return the
 *          original workflow_id and the response body byte-for-byte.
 *
 * UX RULES:
 *   - ESC closes (non-regulatory dialog).
 *   - Submit is primary; Cancel is secondary.
 *   - Server-issued workflow_id is surfaced to the parent so it can navigate
 *     to /workflows/{id} on success.
 *   - The signature gate (AUTHORSHIP signature) happens AFTER this modal
 *     succeeds, in a downstream SignatureConfirmationModal launched by the
 *     parent component. This modal does NOT collect signature material.
 *
 * URS rows: WF-008 (idempotency on workflow start), WF-013 (FSM kicks
 *           DRAFT→IN_REVIEW via START_WORKFLOW), I18N-001/002.
 * ============================================================================
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@edms/design-system';
import { useStartWorkflow, type StartWorkflowResponse } from '../../api/hooks';
import { useReviewerChain } from '../../api/orgHooks';
import styles from './NewDocumentForm.module.css';  // shared dialog styles

/** Workflow templates per drafts/03_prd §5.3 + workflow-fsm.feature. */
export const WORKFLOW_TEMPLATES = [
  { code: 'WF-STD-1', labelKey: 'startWorkflow.templates.std1' },
  { code: 'WF-STD-3', labelKey: 'startWorkflow.templates.std3' },
  { code: 'WF-FAST-CRITICAL', labelKey: 'startWorkflow.templates.fastCritical' },
] as const;

const schema = z.object({
  template_code: z.string().min(1, 'startWorkflow.errors.templateRequired'),
  reviewer_user_ids: z.string().trim().min(1, 'startWorkflow.errors.reviewerRequired'),
  rationale: z.string().trim().min(10, 'startWorkflow.errors.rationaleTooShort').max(1000, 'startWorkflow.errors.rationaleTooLong'),
});
type FormValues = z.infer<typeof schema>;

export interface StartWorkflowModalProps {
  isOpen: boolean;
  /** The draft to attach the workflow to (mandatory). */
  documentVersionId: string;
  /** Human-readable doc identifier shown in the dialog title. */
  documentLabel: string;
  /** Document's department id — when present, the modal defaults to
   *  department-routed reviewers (DEPARTMENT_HEAD → QA_MANAGER) and the
   *  author can opt out to a manual list (WF-031..038). */
  departmentId?: string;
  onClose: () => void;
  onStarted: (response: StartWorkflowResponse) => void;
}

export function StartWorkflowModal({
  isOpen,
  documentVersionId,
  documentLabel,
  departmentId,
  onClose,
  onStarted,
}: StartWorkflowModalProps) {
  const { t } = useTranslation();
  const firstFieldRef = useRef<HTMLSelectElement | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  // When the doc carries a departmentId, default to department-routed review.
  const [routingMode, setRoutingMode] = useState<'auto' | 'manual'>(
    departmentId ? 'auto' : 'manual',
  );
  // DMS-11: keep the common path automatic — the workflow template + manual
  // reviewer entry live under an "Advanced" disclosure. Without a department
  // (rare) the auto chain can't resolve, so Advanced opens by default.
  const [advanced, setAdvanced] = useState(!departmentId);
  const effectiveMode: 'auto' | 'manual' = advanced ? routingMode : 'auto';
  // DMS-20: change-impact classification auto-selects the workflow profile —
  // editorial changes fast-track through a single reviewer; content changes get
  // the full three-reviewer flow. (FAST-CRITICAL stays a manual Advanced choice.)
  const [changeType, setChangeType] = useState<'EDITORIAL' | 'MINOR' | 'MAJOR'>('MINOR');
  const derivedTemplate = changeType === 'EDITORIAL' ? 'WF-STD-1' : 'WF-STD-3';
  const chainQ = useReviewerChain(departmentId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      template_code: 'WF-STD-3',
      reviewer_user_ids: '',
      rationale: '',
    },
  });

  const startMutation = useStartWorkflow();

  useEffect(() => {
    if (isOpen) {
      setServerError(null);
      reset({ template_code: 'WF-STD-3', reviewer_user_ids: '', rationale: '' });
      setRoutingMode(departmentId ? 'auto' : 'manual');
      setAdvanced(!departmentId);
      setChangeType('MINOR');
      setTimeout(() => firstFieldRef.current?.focus(), 0);
    }
  }, [isOpen, reset, departmentId]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  function onBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    setServerError(null);

    // Validate rationale + template; reviewer_user_ids is conditionally required
    // (only in manual routing mode).
    if (!values.template_code) {
      setServerError('startWorkflow.errors.templateRequired');
      return;
    }
    if (!values.rationale || values.rationale.trim().length < 10) {
      setServerError('startWorkflow.errors.rationaleTooShort');
      return;
    }

    let reviewerIds: string[] = [];
    if (effectiveMode === 'auto') {
      // Use the department-derived reviewer chain (DEPARTMENT_HEAD → QA_MANAGER).
      if (!chainQ.data?.chain || chainQ.data.chain.length === 0) {
        setServerError('startWorkflow.errors.chainUnresolved');
        return;
      }
      reviewerIds = chainQ.data.chain.map((c) => c.userId);
    } else {
      reviewerIds = values.reviewer_user_ids
        .split(/[,\n]/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (reviewerIds.length === 0) {
        setServerError('startWorkflow.errors.reviewerRequired');
        return;
      }
    }

    try {
      const response = await startMutation.mutateAsync({
        document_version_id: documentVersionId,
        template_code:       advanced ? values.template_code : derivedTemplate,
        reviewer_user_ids:   reviewerIds,
        rationale:           values.rationale,
      });
      onStarted(response);
      onClose();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'startWorkflow.errors.serverFailed';
      setServerError(msg);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.backdrop} onClick={onBackdropClick} role="presentation">
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="start-workflow-title"
        aria-describedby="start-workflow-desc"
      >
        <header className={styles.header}>
          <h2 id="start-workflow-title" className={styles.title}>
            {t('startWorkflow.title')}
          </h2>
          <p id="start-workflow-desc" className={styles.desc}>
            {t('startWorkflow.subtitle', { document: documentLabel })}
          </p>
        </header>

        <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
          {serverError && (
            <div role="alert" className={styles.errorBanner}>
              {t(serverError, { defaultValue: serverError })}
            </div>
          )}

          {advanced ? (
            <div className={styles.field}>
              <label htmlFor="template_code" className={styles.label}>
                {t('startWorkflow.templateLabel')}
                <span aria-hidden="true" className={styles.required}>*</span>
              </label>
              {(() => {
                const { ref: rhfRef, ...rhfRest } = register('template_code', {
                  required: 'startWorkflow.errors.templateRequired',
                });
                return (
                  <select
                    id="template_code"
                    {...rhfRest}
                    ref={(el) => {
                      rhfRef(el);
                      firstFieldRef.current = el;
                    }}
                    className={styles.select}
                    aria-invalid={errors.template_code ? 'true' : 'false'}
                  >
                    {WORKFLOW_TEMPLATES.map((wt) => (
                      <option key={wt.code} value={wt.code}>
                        {t(wt.labelKey)} ({wt.code})
                      </option>
                    ))}
                  </select>
                );
              })()}
              {errors.template_code && (
                <span className={styles.fieldError} role="alert">
                  {t(errors.template_code.message ?? 'startWorkflow.errors.templateRequired')}
                </span>
              )}
            </div>
          ) : (
            // Common path (DMS-20): the author classifies the change; the workflow
            // template is derived automatically. The raw template stays registered
            // via a hidden input + is overridable under Advanced.
            <div className={styles.field}>
              <label className={styles.label}>
                {t('startWorkflow.changeTypeLabel', { defaultValue: 'Change type' })}
                <span aria-hidden="true" className={styles.required}>*</span>
              </label>
              <div role="radiogroup" aria-label="change type" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {(['EDITORIAL', 'MINOR', 'MAJOR'] as const).map((ct) => (
                  <label key={ct} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="radio" name="changeType" value={ct} checked={changeType === ct} onChange={() => setChangeType(ct)} />
                    <span>{t(`startWorkflow.changeType.${ct}`, { defaultValue: ct.charAt(0) + ct.slice(1).toLowerCase() })}</span>
                  </label>
                ))}
              </div>
              <p className={styles.helperText} style={{ marginTop: 6 }} aria-live="polite">
                {changeType === 'EDITORIAL'
                  ? t('startWorkflow.derivedEditorial', { defaultValue: '→ Editorial: single-reviewer fast-track (WF-STD-1).' })
                  : t('startWorkflow.derivedFull', { defaultValue: '→ Content change: full review, three reviewers (WF-STD-3).' })}
              </p>
              <input type="hidden" {...register('template_code')} />
            </div>
          )}

          <div className={styles.field}>
            <label className={styles.label}>
              {t('startWorkflow.reviewerLabel', { defaultValue: 'Reviewer routing' })}
              <span aria-hidden="true" className={styles.required}>*</span>
            </label>

            {advanced && departmentId && (
              <div role="radiogroup" aria-label="routing mode" style={{ display: 'flex', gap: 16, marginBottom: 6 }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="radio"
                    name="routingMode"
                    value="auto"
                    checked={routingMode === 'auto'}
                    onChange={() => setRoutingMode('auto')}
                  />
                  <span>{t('startWorkflow.routingAuto', { defaultValue: 'Department hierarchy (recommended)' })}</span>
                </label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="radio"
                    name="routingMode"
                    value="manual"
                    checked={routingMode === 'manual'}
                    onChange={() => setRoutingMode('manual')}
                  />
                  <span>{t('startWorkflow.routingManual', { defaultValue: 'Pick reviewers manually' })}</span>
                </label>
              </div>
            )}

            {effectiveMode === 'auto' ? (
              <div className={styles.helperText} aria-live="polite" style={{ padding: '8px 12px', background: 'var(--neutral-25, #FAFBFC)', borderRadius: 6, border: '1px solid var(--line-default, #DCE3EB)' }}>
                {chainQ.isLoading && t('startWorkflow.chainLoading', { defaultValue: 'Resolving reviewer chain…' })}
                {chainQ.data?.chain && chainQ.data.chain.length > 0 && (
                  <>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {t('startWorkflow.chainTitle', { defaultValue: 'Will route through:' })}
                    </div>
                    <ol style={{ margin: 0, paddingInlineStart: 18 }}>
                      {chainQ.data.chain.map((c) => (
                        <li key={c.userId}>
                          <strong>{c.userName}</strong>
                          <span style={{ color: 'var(--text-tertiary, #6B7989)', marginInlineStart: 6 }}>
                            ({c.positionKind === 'DEPARTMENT_HEAD' ? 'Dept Head' : 'QA Manager'})
                          </span>
                          {c.backupUserName && (
                            <span style={{ color: 'var(--text-tertiary, #6B7989)', marginInlineStart: 6 }}>
                              · backup: {c.backupUserName}
                            </span>
                          )}
                        </li>
                      ))}
                    </ol>
                  </>
                )}
                {chainQ.data?.chain && chainQ.data.chain.length === 0 && (
                  <span style={{ color: 'var(--status-danger-text, #B91C1C)' }}>
                    {t('startWorkflow.chainEmpty', { defaultValue: 'No reviewer chain configured for this department.' })}
                  </span>
                )}
              </div>
            ) : (
              <>
                <input
                  id="reviewer_user_ids"
                  type="text"
                  {...register('reviewer_user_ids')}
                  className={styles.input}
                  aria-invalid={errors.reviewer_user_ids ? 'true' : 'false'}
                  placeholder={t('startWorkflow.reviewerPlaceholder')}
                  autoComplete="off"
                />
                {errors.reviewer_user_ids && (
                  <span className={styles.fieldError} role="alert">
                    {t(errors.reviewer_user_ids.message ?? 'startWorkflow.errors.reviewerRequired')}
                  </span>
                )}
              </>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="rationale" className={styles.label}>
              {t('startWorkflow.rationaleLabel')}
              <span aria-hidden="true" className={styles.required}>*</span>
            </label>
            <textarea
              id="rationale"
              {...register('rationale', {
                required: 'startWorkflow.errors.rationaleRequired',
                minLength: { value: 10, message: 'startWorkflow.errors.rationaleTooShort' },
                maxLength: { value: 1000, message: 'startWorkflow.errors.rationaleTooLong' },
              })}
              className={styles.input}
              rows={4}
              aria-invalid={errors.rationale ? 'true' : 'false'}
              maxLength={1000}
              placeholder={t('startWorkflow.rationalePlaceholder')}
            />
            {errors.rationale && (
              <span className={styles.fieldError} role="alert">
                {t(errors.rationale.message ?? 'startWorkflow.errors.rationaleRequired')}
              </span>
            )}
          </div>

          {departmentId && (
            <button
              type="button"
              onClick={() => setAdvanced((a) => !a)}
              style={{ background: 'none', border: 'none', color: 'var(--color-primary, #1d4ed8)', cursor: 'pointer', padding: 0, marginTop: 4, fontSize: 13, textAlign: 'start' }}
            >
              {advanced
                ? t('startWorkflow.hideAdvanced', { defaultValue: '▴ Hide advanced options' })
                : t('startWorkflow.showAdvanced', { defaultValue: '▾ Advanced options (template, manual reviewers)' })}
            </button>
          )}

          <footer className={styles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting || startMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting || startMutation.isPending}
            >
              {t('startWorkflow.submit')}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
